import React, { useState } from 'react';
import styles from '../../../styles/AgencyInformation.module.css';
import profileStyles from '../../../styles/AccountProfile.module.css';
import modalStyles from '../../../styles/AccountModal.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import InvitationForm from '../ManageAgency/InvitationForm';
import { UserDetails } from '../../../types/User';
import ChangePackageModal from './EditPackage';
import { useUserDetailsApi } from '../../../hooks/useUserDetails';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';
import { PermissionGate } from '../../common/PermissionGate';
import type { PendingInvite } from '../../../types/User';

interface AgencyUsersProps {
    // Removed isManager prop - will use enhanced auth permissions
    agencyUsers: UserDetails[];
    pendingInvites: PendingInvite[];
    userId: string;
    removeLoading: string | null;
    onRemoveUser: (userId: string) => void;
    onDeleteUser: (userId: string) => void;
    onInviteUser: (email: string, role: string) => void;
    onCancelInvite: (inviteId: string) => void;
    inviteLoading: boolean;
    agency: any;
}

const AgencyUsers: React.FC<AgencyUsersProps> = ({
    agencyUsers,
    pendingInvites,
    userId,
    removeLoading,
    onRemoveUser,
    onDeleteUser,
    onInviteUser,
    onCancelInvite,
    inviteLoading,
    agency,
}) => {
    const { changePackageSize, baselinePackageCost, perSeatCost } = useUserDetailsApi();
    const { updateAgencyDetails, hasPermission } = useEnhancedAuth();
    
    // Permission-based access control
    const canManageUsers = hasPermission('user_manage_agency');

    const [showInviteForm, setShowInviteForm] = useState(false);
    const [showChangePackage, setShowChangePackage] = useState(false);
    const [confirmChange, setConfirmChange] = useState<{ newSize: number, direction: 'up' | 'down', amountDue: number, newPrice: number } | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);

    // Calculate current price and per-seat cost
    const currentSize = agency?.numberOfSeats;
    const currentPrice = agency?.packagePrice;

    // Calculate seat usage
    const maxSeats = agency?.numberOfSeats || 1;
    const usedSeats = agencyUsers.length + 1; // +1 for manager
    const seatsFull = usedSeats >= maxSeats;

    // Called from ChangePackageModal
    const handleChangePackageSubmit = (newSize: number) => {
        if (newSize === currentSize) {
            setInfoMessage('No change to package size.');
            setShowChangePackage(false);
            return;
        }
        const newPrice = baselinePackageCost + newSize * perSeatCost;
        const amountDue = newPrice - currentPrice;
        setConfirmChange({
            newSize,
            direction: newSize > currentSize ? 'up' : 'down',
            amountDue: Math.abs(amountDue),
            newPrice
        });
        setShowChangePackage(false);
    };

    // After user confirms in confirmation modal
    const handleConfirmChange = async () => {
        if (!confirmChange) return;
        try {
            const resp = await changePackageSize(agency.agencyId, confirmChange.newSize);
            if (resp && !resp.error) {
                updateAgencyDetails(resp);
                setInfoMessage('Package size updated successfully!');
            } else if (resp && resp.error) {
                setInfoMessage(`Error updating package size: ${resp.error}`);
            }
        } catch (err: any) {
            // Axios throws for non-2xx responses
            if (err.response && err.response.status === 402) {
                setInfoMessage('Payment failed. Please try again or contact support.');
            } else {
                setInfoMessage('An unexpected error occurred.');
            }
        }
        setConfirmChange(null);
    };

    return (
        <div className={profileStyles.usersSection}>
            <div className={profileStyles.usersSectionHeader}>
                <h3 className={profileStyles.usersSectionTitle}>Agency Users</h3>
                <div className={profileStyles.usersSectionActions}>
                    <PermissionGate requiredPermissions={["user_invite"]}>
                        <button
                            className={`${buttonStyles.button} ${buttonStyles.primary}`}
                            onClick={() => {
                                if (!seatsFull) setShowInviteForm(true);
                            }}
                            title={seatsFull ? 'No available seats. Please increase your package size.' : 'Invite User'}
                            disabled={seatsFull}
                        >
                            Invite User
                        </button>
                    </PermissionGate>
                    <PermissionGate requiredPermissions={["agency_manage_own"]}>
                        <button
                            className={`${buttonStyles.button} ${buttonStyles.primary}`}
                            onClick={() => setShowChangePackage(true)}
                            title="Change Package"
                        >
                            Change Package
                        </button>
                    </PermissionGate>
                </div>
            </div>

            {/* Change Package Modal */}
            {showChangePackage && (
                <ChangePackageModal
                    currentSize={agency?.numberOfSeats}
                    currentCost={agency?.packagePrice}
                    minSize={agencyUsers.length + 1} // total users (including manager)
                    onClose={() => setShowChangePackage(false)}
                    onSubmit={handleChangePackageSubmit}
                />
            )}
            {/* Confirmation Modal BEFORE API call */}
            {confirmChange && (
                <div className={modalStyles.backdrop}>
                    <div className={`${modalStyles.modal} ${modalStyles.modalSm}`}>
                        <div className={modalStyles.header}>
                            <h3 className={modalStyles.title}>Confirm Package Change</h3>
                            <button
                                type="button"
                                className={modalStyles.closeButton}
                                onClick={() => setConfirmChange(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className={modalStyles.body}>
                            {confirmChange.direction === 'up' ? (
                                <p className={modalStyles.staticValue}>You are increasing your package size. You will be charged <b>R{confirmChange.amountDue}</b> immediately for the difference. Continue?</p>
                            ) : (
                                <p className={modalStyles.staticValue}>You are reducing your package size. The change will take effect at the next billing cycle. No immediate refund or charge will occur. Continue?</p>
                            )}
                        </div>
                        <div className={modalStyles.footer}>
                            <button className={buttonStyles.button + ' ' + buttonStyles.secondary} onClick={() => setConfirmChange(null)}>
                                Cancel
                            </button>
                            <button className={buttonStyles.button + ' ' + buttonStyles.primary} onClick={handleConfirmChange}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Info Message Modal */}
            {infoMessage && (
                <div className={modalStyles.backdrop}>
                    <div className={`${modalStyles.modal} ${modalStyles.modalSm}`}>
                        <div className={modalStyles.header}>
                            <h3 className={modalStyles.title}>Notice</h3>
                            <button
                                type="button"
                                className={modalStyles.closeButton}
                                onClick={() => setInfoMessage(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className={modalStyles.body}>
                            <p className={modalStyles.staticValue}>{infoMessage}</p>
                        </div>
                        <div className={modalStyles.footer}>
                            <button className={buttonStyles.button + ' ' + buttonStyles.primary} onClick={() => setInfoMessage(null)}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.agencyUsersMainRow}>
                <div className={styles.agencyUsersLeftCol}>
                    {/* Pending Invites List */}
                    {pendingInvites.length > 0 && (
                        <div className={styles.pendingInvitesList}>
                            <div className={styles.pendingInvitesHeader}>Pending Invitations</div>
                            {pendingInvites.map((invite: PendingInvite) => (
                                <div key={invite.id} className={styles.pendingInviteItem}>
                                    <span className={styles.pendingInviteEmail}>{invite.recipientEmail}</span>
                                    <span className={styles.pendingInviteRole}>{invite.role?.name || ''}</span>
                                    <span className={styles.pendingInviteStatus}>{invite.status}</span>
                                    {canManageUsers && (
                                        <button
                                            className={`${buttonStyles.button} ${buttonStyles.secondary}`}
                                            title="Cancel Invitation"
                                            onClick={() => onCancelInvite(invite.id.toString())}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Users List */}
                    {agencyUsers.length > 0 ? (
                        <div className={styles.usersList}>
                            {agencyUsers.filter((u: UserDetails) => u.id !== userId).map((user: UserDetails, index: number) => (
                                <div key={user.id || index} className={styles.userItem}>
                                    <div className={styles.userInfo}>
                                        <span className={styles.userName}>{user.fullName}</span>
                                        <span className={styles.userEmail}>{user.email}</span>
                                    </div>
                                    <span className={styles.userRole}>{user.roleName}</span>
                                    <span className={styles.userActions}>
                                        {canManageUsers && (
                                            <button
                                                className={`${buttonStyles.button} ${buttonStyles.danger}`}
                                                title="Remove User"
                                                onClick={() => onRemoveUser(user.id)}
                                                disabled={removeLoading === user.id}
                                            >
                                                {removeLoading === user.id ? '-' : '-'}
                                            </button>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={profileStyles.emptyState}>
                            <span className={profileStyles.emptyStateIcon}>👥</span>
                            <span className={profileStyles.emptyStateText}>No other users found</span>
                        </div>
                    )}
                </div>
                <div className={styles.agencyUsersRightCol}>
                    <PermissionGate requiredPermissions={["agency_manage_own"]}>
                        <div className={styles.agencySeatInfo}>
                            <div className={styles.agencySeatInfoItem}>
                                <label className={styles.agencySeatInfoLabel}>Total Seats Available: {agency?.numberOfSeats}</label>
                            </div>
                            <div className={styles.agencySeatInfoItem}>
                                <label className={styles.agencySeatInfoLabel}>Total Seats Used: {agencyUsers.length + 1}</label>
                            </div>
                        </div>
                    </PermissionGate>
                </div>
            </div>
            {/* Invite User Dialog */}
            {showInviteForm && (
                <div className={modalStyles.backdrop}>
                    <div className={`${modalStyles.modal} ${modalStyles.modalSm}`}>
                        <div className={modalStyles.header}>
                            <h3 className={modalStyles.title}>Invite User</h3>
                            <button
                                type="button"
                                className={modalStyles.closeButton}
                                onClick={() => setShowInviteForm(false)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className={modalStyles.body}>
                            {seatsFull ? (
                                <div className={modalStyles.warning}>
                                    You have reached your agency's seat limit. Please increase your package size to invite more users.
                                </div>
                            ) : (
                                <InvitationForm
                                    onSubmit={(email, role) => {
                                        onInviteUser(email, role);
                                        setShowInviteForm(false);
                                    }}
                                    onCancel={() => setShowInviteForm(false)}
                                />
                            )}
                        </div>
                        {seatsFull && (
                            <div className={modalStyles.footer}>
                                <button className={buttonStyles.button + ' ' + buttonStyles.primary} onClick={() => setShowInviteForm(false)}>
                                    OK
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgencyUsers;
